import React, {useEffect, useMemo, useRef} from 'react';
import {store, useAppDispatch, useAppSelector} from "../../app/store";
import {
    clearError,
    continueGameSession, endGameSession,
    pauseGameSession,
    questionAnswer, sessionIsAlive, setEndFormData,
    startGameSession, submitEndForm
} from "../../app/slices/gameSession";
import QuestionContent from "./QuestionContent";
import {
    QuestionTypeConnect,
    QuestionTypeNumberScale,
    QuestionTypeOrder,
    QuestionTypeSelect,
    QuestionTypeText
} from "../../types/gameSession";
import {Language, translations} from "../../translations";
import toast from "react-hot-toast";
import GameSlide from "./GameSlide";
import {ReactComponent as InfoIcon} from '../../images/icons/circle-info-regular.svg';
import {CSSTransition} from 'react-transition-group';
import {TRANSITION_DURATION} from './Game';
import CircleProgress from './CircleProgress';
import FormFields from "./FormFields";
import {FormField} from "../../types/games";
import GameElement from "./GameElement";
import Timer from "./Timer";
import {EyeSlashFill} from "react-bootstrap-icons";
import {Modal, Button} from 'react-bootstrap';

type Props = {
    game_id: number;
    lang: Language;
    isHidden: boolean;
    onBackToStart: () => void;
}

/*const testQuestion: QuestionTypeNumberScale = {
    type: 'number_scale',
    title: 'Test question',
    rating_type: 'default',
    description: 'This is a test question',
    outro_text: 'This is the outro text',
    allow_skip: true,
    id: 1,
    answered: false,
    time_limit: 60,
    options: [
        {
            key: '0',
            option: 'Option 0',
            rating: 0,
            selected: false
        },
        {
            key: '1',
            option: '',
            rating: 1,
            selected: false
        },
        {
            key: '2',
            option: '',
            rating: 2,
            selected: false
        },
        {
            key: '3',
            option: '',
            rating: 3,
            selected: false
        },
        {
            key: '4',
            option: '',
            rating: 4,
            selected: false
        },
        {
            key: '5',
            option: 'CENTER',
            rating: 5,
            selected: false
        },
        {
            key: '6',
            option: '',
            rating: 6,
            selected: false
        },
        {
            key: '7',
            option: '',
            rating: 7,
            selected: false
        },
        {
            key: '8',
            option: '',
            rating: 8,
            selected: false
        },
        {
            key: '9',
            option: '',
            rating: 9,
            selected: false
        },
        {
            key: '10',
            option: 'Option 10',
            rating: 10,
            selected: false
        }
    ]
}*/

const GameSession = ({game_id, lang, isHidden, onBackToStart}: Props) => {
    const dispatch = useAppDispatch();
    const {
        session_data,
        loaded,
        error,
        transition,
        showPointsDiff,
        redirectUrl,
        endFormSuccess
    } = useAppSelector((state) => state.gameSession);
    const {data} = useAppSelector((state) => state.game);
    const LANG = translations[lang];
    const [isVisible, setIsVisible] = React.useState(false);
    const [returningToStart, setReturningToStart] = React.useState(false);
    const [slideTransition, setSlideTransition] = React.useState<"slide-right" | "slide-left">("slide-right");
    const [nextBtnClassName, setNextBtnClassName] = React.useState('circle-button pulse active');
    const [isTimerActive, setIsTimerActive] = React.useState(false);

    const isProcessingRef = useRef(false);
    const transRef = React.useRef<HTMLDivElement | null>(null);
    const [showTerms, setShowTerms] = React.useState(false);
    const hasEndSuccessShown = useRef(false);

    const btnDisabled = !loaded || transition || returningToStart || isHidden;
    useEffect(() => {
        // NOTE: on React.StrictMode, this useEffect is called twice on dev mode
        if (loaded) return;
        const form_fields = data?.form;
        dispatch(startGameSession({form_fields, game_id: game_id, language: lang}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded]);

    // Open terms modal if URL hash is #terms
    useEffect(() => {
        if (window.location && window.location.hash === '#terms') {
            setShowTerms(true);
        }
    }, []);

    // Also react to hash changes to support links that update the URL
    useEffect(() => {
        const onHashChange = () => {
            if (window.location.hash === '#terms') {
                setShowTerms(true);
            }
        };
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    // Delegate clicks on #terms links to open modal (both in end message and form rich texts)
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            const anchor = target.closest('a') as HTMLAnchorElement | null;
            if (!anchor) return;
            const href = (anchor.getAttribute('href') || '').trim().toLowerCase();
            const hash = (anchor.hash || '').toLowerCase();
            if (href === '#terms' || hash === '#terms' || href.endsWith('#terms')) {
                e.preventDefault();
                setShowTerms(true);
            }
        };
        document.addEventListener('click', handleClick);
        return () => {
            document.removeEventListener('click', handleClick);
        };
    }, []);

    // call setInterval every 3 minutes to keep the session alive
    useEffect(() => {
        const interval = setInterval(() => {
            if (typeof (session_data) !== "undefined") {
                dispatch(sessionIsAlive({game_session_id: session_data.id}));
            }
        }, 3 * 60 * 1000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // pause the game session when the user closes the tab
    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (typeof (session_data) !== "undefined") {
                dispatch(pauseGameSession({game_session_id: session_data.id}));
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (redirectUrl !== null) {
            window.location.href = redirectUrl;
        }
    }, [redirectUrl]);

    const questionOptions = (session_data?.question as QuestionTypeSelect)?.options ?? [];

    useEffect(() => {
        // trigger pulse animation when user changes the selected options
        if (session_data?.question?.type === 'select') {
            setNextBtnClassName('circle-button active');
            setTimeout(() => {
                setNextBtnClassName('circle-button pulse active');
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(questionOptions)]);


    useEffect(() => {
        if (isHidden) setSlideTransition("slide-right");
        if (loaded && !transition && !isHidden && !returningToStart) {
            window.scrollTo({top: 1, behavior: 'instant'});
        }
        setIsVisible(loaded && !transition && !isHidden && !returningToStart);
    }, [loaded, transition, isHidden, returningToStart]);


    // Do not show separate toast for end form; inline thank-you is shown instead
    useEffect(() => {
        if (endFormSuccess && !hasEndSuccessShown.current) {
            hasEndSuccessShown.current = true;
        }
    }, [endFormSuccess]);

    const handleContinue = () => {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;

        if (session_data?.content_id !== undefined) {
            setSlideTransition("slide-right");
            dispatch(continueGameSession({content_id: session_data.content_id, back: false})).finally(() => {
                isProcessingRef.current = false;
            });
        }
    };

    const handleQuit = () => {
        if (window.confirm(LANG.confirm_quit_game)) {
            dispatch(endGameSession());
        }
    };

    const handleBack = () => {
        if (btnDisabled) return;
        if (session_data?.number === 1) {
            setSlideTransition("slide-left");
            setReturningToStart(true);
            return;
        }
        if (session_data?.content_id !== undefined) {
            setSlideTransition("slide-left");
            dispatch(continueGameSession({content_id: session_data.content_id, back: true}))
        }
    }

    let questionInfo;
    switch (session_data?.question?.type) {
        case 'select':
            const question = session_data.question as QuestionTypeSelect;
            if (question.multiple) {
                questionInfo = LANG.select_multiple_info;
            } else {
                questionInfo = LANG.select_single_info;
            }
            break;
        case 'order':
            questionInfo = LANG.order_info;
            break;
        case 'connect':
            questionInfo = LANG.connect_info;
            break;
        default:
            questionInfo = '';
    }

    const handleAnswer = (skip?: boolean) => {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;

        setSlideTransition("slide-right");

        // Fetch the latest session_data inside the function
        const latestSessionData = store.getState().gameSession.session_data;

        let payload: any = {
            content_id: latestSessionData?.content_id,
        };

        switch (latestSessionData?.question?.type) {
            case 'select': {
                const question = latestSessionData.question as QuestionTypeSelect;
                payload.answers = question.options
                    .filter(option => option.selected)
                    .map(({ index, answer }) => ({ index, answer: answer || null }));
                break;
            }
            case 'order': {
                const orderQuestion = latestSessionData.question as QuestionTypeOrder;
                payload.answers = orderQuestion.options.map(option => option.index);
                break;
            }
            case 'connect': {
                const connectQuestion = latestSessionData.question as QuestionTypeConnect;
                payload.answers = connectQuestion.selected_pairs;
                break;
            }
            case 'text': {
                const textQuestion = latestSessionData.question as QuestionTypeText;
                payload.answers = textQuestion.options;
                break;
            }
            case 'number_scale': {
                const numberScaleQuestion = latestSessionData.question as QuestionTypeNumberScale;
                payload.answers = [numberScaleQuestion.options.find(option => option.selected)?.rating];
                break;
            }
            default:
                break;
        }

        if (skip) {
            payload.skip = true;
            payload.answers = [];
        }

        dispatch(questionAnswer(payload)).finally(() => {
            isProcessingRef.current = false;
        });
    };

    const isValid = session_data?.end?.form.every((field: FormField) => !field.required || (field.required && field.value !== ''));

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        dispatch(setEndFormData({[name]: value}));
    }

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, checked} = e.target;
        dispatch(setEndFormData({[name]: checked ? '1' : ''}));
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && isValid) {
            event.preventDefault();
            event.stopPropagation();
            handleEndFormSubmit(event);
        }
    }

    const handleEndFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (session_data && session_data.end && session_data.end.form && session_data.id) {
            dispatch(submitEndForm({session_id: session_data.id, form_fields: session_data.end.form}))
        }
    };

    if (error) {
        toast.error(error, {position: 'top-right',});
        dispatch(clearError());
    }

    let allowAnswer = false;
    if (session_data && session_data.question) {
        if (session_data.question.answered) {
            allowAnswer = true;
        } else if (session_data.question.type === 'select') {
            const question = session_data.question as QuestionTypeSelect;
            allowAnswer = question.options.some(option => option.selected);
        } else if (session_data.question.type === 'order') {
            const question = session_data.question as QuestionTypeOrder;
            allowAnswer = question.options.some(option => option.index !== null);
        } else if (session_data.question.type === 'connect') {
            const question = session_data.question as QuestionTypeConnect;
            allowAnswer = question.selected_pairs.length === question.options1.length;
        } else if (session_data.question.type === 'number_scale') {
            const question = session_data.question as QuestionTypeNumberScale;
            allowAnswer = question.options.some(option => option.selected);
        } else if (session_data.question.type === 'text') {
            const question = session_data.question as QuestionTypeText;
            allowAnswer = question.options.every(item => {
                const isRequired = Boolean(item.required); // Convert to boolean
                const hasValue = item.value !== null && item.value !== undefined && item.value !== '';
                return !isRequired || (isRequired && hasValue);
            });
        }
    }

    let content;
    let backBtn = (
        <div className="circle-button">
            <div className="triangle-left"></div>
        </div>
    );
    let nextBtn = (
        <div className="circle-button">
            <div className="triangle-right"></div>
        </div>
    );

    const timer = useMemo(() => {
        const question = session_data?.question;
        if (!question?.id || question.answered || !question.time_limit) {
            setIsTimerActive(false);
            return null;
        }

        setIsTimerActive(true);

        return (
            <Timer
                timeLimit={question.time_limit}
                onComplete={() => {
                    handleAnswer();
                    setIsTimerActive(false);
                }}
                key={question.id}
                isActive={!question.answered}
            />
        );
    }, [session_data?.question?.id, session_data?.question?.answered]);

    let circleProgress = null;

    if (typeof session_data !== 'undefined') {
        circleProgress =
            <CircleProgress timerActive={isTimerActive} session_data={session_data} showPointsDiff={showPointsDiff}
                            lang={lang}/>;
    }

    if (session_data && session_data.question) {
        if (!btnDisabled) {
            backBtn = (
                <div className="circle-button active" onClick={() => handleBack()}>
                    <div className="triangle-left"></div>
                </div>
            );
        }

        if (!btnDisabled && allowAnswer) {
            if (session_data.question.answered) {
                nextBtn = (
                    <div className="circle-button pulse active" onClick={() => handleContinue()}>
                        <div className="triangle-right"></div>
                    </div>
                );
            } else {
                nextBtn = (
                    <div className={nextBtnClassName} onClick={() => handleAnswer()}>
                        <div className="triangle-right"></div>
                    </div>
                );
            }
        }


        const showDescriptionWrapper = session_data.question.description || questionInfo;
        content = (
            <div
                className={`game-container ${session_data && session_data.question && session_data.question?.answered ? 'answered' : ''}`}>
                <div className='game-card-header'>
                    <h1>{session_data.question.title}</h1>
                    {showDescriptionWrapper && (
                        <div className='description-wrapper'>
                            {session_data.question.description && (
                                <div
                                    className='description-text'
                                    dangerouslySetInnerHTML={{__html: session_data.question.description}}
                                />
                            )}
                            {questionInfo && (
                                <div className='question-info'>
                                    <InfoIcon/>
                                    <span>{questionInfo}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className='game-card-body'>
                    <QuestionContent question={session_data.question}
                                     showPoints={session_data.points !== null}
                                     LANG={LANG}/>
                    {session_data.question && session_data.question.allow_skip && (
                        <div className='pb-3 mt-3 text-center'>
                            <span className='cursor-pointer' aria-label={LANG.skip_question} onClick={() => handleAnswer(true)}><EyeSlashFill /> {LANG.skip_question}</span>
                        </div>
                    )}

                    {session_data.question.outro_text &&
                        <div
                            className={`outro-text ${session_data.question.outro_text ? 'outro-visible' : ''}`}
                            dangerouslySetInnerHTML={{__html: session_data.question.outro_text}}>
                        </div>
                    }
                </div>
                <div className='glowing-bar'></div>
            </div>
        );
    } else if (session_data && session_data.end) {
        backBtn = (
            <div className="circle-button active" onClick={() => handleBack()}>
                <div className="triangle-left"></div>
            </div>
        );
        const showEndPoints = session_data.end.display_end_screen_points;
        const disableForm = !!endFormSuccess;

        circleProgress = null;
        nextBtn = (<div></div>);
        content = (
            <div className='game-container game-end-screen'>
                <div className='game-card-body'>
                    <div className='container-fluid'>
                        <div className='row'>
                            <div className='col-12 col-md-8 offset-md-2 d-flex flex-column align-items-center'>
                                <h1>{session_data.end.title}</h1>
                                {session_data.end.points_message &&
                                    <p className='game-end-points-message'>{session_data.end.points_message}</p>
                                }
                                {showEndPoints && session_data.points !== null && session_data.max_points > 0 &&
                                    <div className='game-end-points'>
                                        <h3>{LANG.points_scored}:</h3>
                                        <div className='points-container'>
                                            <span className='points-scored'>{session_data.points}</span> <span
                                            className='points-separator'>/</span> <span
                                            className='max-points'>{session_data.max_points}</span>
                                        </div>
                                    </div>
                                }
                                {showEndPoints && session_data.end.average_points !== null && session_data.max_points !== null && session_data.max_points > 0 &&
                                    <div className='game-end-average'>
                                        {LANG.all_players_average}: <span className='average-points'>{session_data.end.average_points}</span>
                                    </div>

                                }
                                {!endFormSuccess && session_data.end.end_message !== "" &&
                                    <div className='game-end-message pb-3'
                                         dangerouslySetInnerHTML={{__html: session_data.end.end_message}}/>
                                }
                                {
                                    (!session_data.end.form || session_data.end.form.length === 0) && (
                                        <button
                                            className='btn game-btn game-btn-secondary mb-4 mt-2'
                                            onClick={handleQuit}>
                                            {LANG.quit_game}
                                        </button>
                                    )
                                }
                            </div>
                            {session_data.end.form && session_data.end.form.length > 0 && (
                                <div className='col-12 col-md-8 offset-md-2'>
                                    {endFormSuccess ? (
                                        <>
                                            {session_data.end.end_form_thanks ? (
                                                <div className='game-end-thanks pb-4 text-center' dangerouslySetInnerHTML={{__html: session_data.end.end_form_thanks}} />
                                            ) : (
                                                <p className='game-end-thanks pb-4 text-center'>{endFormSuccess}</p>
                                            )}
                                            <div className='d-flex justify-content-center'>
                                                <button className='btn game-btn game-btn-secondary'
                                                        onClick={() => dispatch(endGameSession())}>{LANG.quit_game}</button>
                                            </div>
                                        </>
                                    ) : (
                                        <form onSubmit={handleEndFormSubmit} id='game-end-form' className='row gy-2' method="POST">
                                            <FormFields form={session_data.end.form}
                                                        disableForm={disableForm}
                                                        handleCheckboxChange={handleCheckboxChange}
                                                        handleInputChange={handleInputChange}
                                                        onKeyDown={onKeyDown}/>
                                            <div className='col-12 d-flex flex-column align-items-center gap-2 pt-4'>
                                                <button disabled={disableForm} type='submit'
                                                        className='btn game-btn'>{LANG.submit}</button>
                                                <button type='button' className='game-end-exit-link'
                                                        onClick={handleQuit}>{LANG.quit_game}</button>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    } else if (session_data && session_data.element) {
        content = (
            <GameElement element={session_data.element}/>
        )
        if (!btnDisabled) {
            backBtn = (
                <div className="circle-button active" onClick={() => handleBack()}>
                    <div className="triangle-left"></div>
                </div>
            );
            nextBtn = (
                <div className="circle-button pulse active" onClick={() => handleAnswer()}>
                    <div className="triangle-right"></div>
                </div>
            );
        }
    } else if (session_data && session_data.slide) {
        content = (
            <GameSlide slide={session_data.slide}/>)
        if (!btnDisabled) {
            backBtn = (
                <div className="circle-button active" onClick={() => handleBack()}>
                    <div className="triangle-left"></div>
                </div>
            );
            nextBtn = (
                <div className="circle-button pulse active" onClick={() => handleAnswer()}>
                    <div className="triangle-right"></div>
                </div>
            );
        }
    } else {
        content = '';
    }

    return (
        <>
            <CSSTransition nodeRef={transRef} in={isVisible} unmountOnExit timeout={TRANSITION_DURATION}
                           classNames={slideTransition} className='slide-outer'
                           onExited={() => {
                               if (returningToStart) {
                                   onBackToStart();
                                   setReturningToStart(false);
                               }
                           }}>
                <div ref={transRef} style={isHidden ? {display: 'none'} : undefined}>
                    {content}
                </div>
            </CSSTransition>
            <div className='game-shader' style={isHidden ? {display: 'none'} : undefined}></div>
            <div className={`game-bottom-bar${returningToStart ? ' game-bottom-bar-leaving' : ''}`}
                 style={isHidden ? {display: 'none'} : undefined}>
                {backBtn}
                {timer}
                {circleProgress}
                {nextBtn}
            </div>

            <Modal show={showTerms && !isHidden} onHide={() => setShowTerms(false)} centered>
                <Modal.Header closeButton />
                <Modal.Body>
                    <div dangerouslySetInnerHTML={{ __html: data?.terms || '' }} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowTerms(false)}>
                        {LANG.close}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default GameSession;
